import type { QueryClient } from "@tanstack/react-query";

import { shownDossierId } from "./api/client";
import type { ExerciseParameters, NamedPlace, WeatherCustom, WeatherOptions } from "./api/types";
import { k, lang, locale, t, tc } from "./i18n/i18n";

/** Météo des Données initiales et de l'étape Terrain : « Saisonnière » (`weather_scenario` « auto », défaut) ou
 *  « Choisie » (`weather_scenario` « custom » et `weather_custom`) ; un dossier importé peut porter un scénario du
 *  catalogue (tout autre identifiant). Le serveur lit le choix sous la forme d'un identifiant de scénario
 *  (`weatherKey`, même forme que `environment.custom_key`) et applique la météo ramenée au terrain
 *  (`weather_settle.applied_key`) : ce module ne fait que présenter. Un seul vocabulaire pour toute l'interface
 *  (tuiles, aperçu, résumés, libellés composés de la logistique). */
export const SEASONAL = "auto";

/** Nom de la météo saisonnière comme VALEUR (réglage, résumés, import, vérification, modes d'action, report d'un
 *  ordre reçu) : celui de l'option du réglage, partout le même. */
export function seasonalLabel(): string {
  return t("Saisonnière");
}
export const CUSTOM = "custom";
export const WIND_FROM_SKY = "sky";

/** Ciel et précipitations, dans l'ordre du sélecteur (identifiants des scénarios du serveur). */
export const SKY_LABELS: Record<string, string> = {
  clair: k("Clair"),
  voile: k("Voilé"),
  couvert: k("Couvert"),
  brume: k("Brume"),
  brouillard: k("Brouillard"),
  brouillard_givrant: k("Brouillard givrant"),
  crachin: k("Crachin"),
  pluie: k("Pluie"),
  pluie_forte: k("Pluie forte"),
  orage: k("Orage"),
  cevenol: k("Épisode cévenol"),
  neige_legere: k("Neige faible"),
  neige_forte: k("Neige forte"),
  // Précipitation qui gèle au sol (le catalogue dit « Pluies verglaçantes ») : « verglas » nomme la glace au sol.
  verglas: k("Pluie verglaçante"),
  mousson: k("Mousson"),
  chaleur_humide: k("Chaleur humide"),
  vent_de_sable: k("Vent de sable"),
  tempete_de_sable: k("Tempête de sable"),
  orage_crue: k("Orage et crue subite"),
  blizzard: k("Blizzard"),
};

/** Clés d'aperçu propres au jeu saisonnier ou à un scénario du catalogue sans ciel (`environment.PICTOGRAM_EXTRAS`). */
const PICTOGRAM_LABELS: Record<string, string> = {
  averses: k("Averses"),
  chaleur: k("Chaleur"),
  froid: k("Froid"),
  vent: k("Vent"),
  degel: k("Dégel"),
};

/** Zones climatiques du serveur (`CampProfile.climate_zone`), un seul nom par climat (celui du réglage « Climat »). */
const CLIMATE_LABELS: Record<string, string> = {
  oceanic: k("climat océanique"),
  semiContinental: k("climat semi-continental"),
  mediterranean: k("climat méditerranéen"),
  highland: k("climat montagnard"),
  tropical: k("climat tropical"),
  subtropicalMonsoon: k("climat subtropical de mousson"),
  arid: k("climat aride"),
  subarctic: k("climat subarctique"),
};

/** Vents du sélecteur (tuiles) : mêmes forces que le serveur (« vent faible » de ses libellés). */
export const WIND_LABELS: Record<string, string> = {
  sky: k("Selon le ciel"),
  faible: k("Faible"),
  modere: k("Modéré"),
  fort: k("Fort"),
  tempete: k("Tempête"),
  regional: k("Vent régional"),
};

/** Vents dans une phrase (« Brouillard, vent faible, 2 à 8 °C ») : forme des libellés du serveur. */
const WIND_PHRASES: Record<string, string> = {
  faible: k("vent faible"),
  modere: k("vent modéré"),
  fort: k("vent fort"),
  tempete: k("tempête"),
  regional: k("vent régional"),
};

/** Vents régionaux nommés (profil du camp). */
const REGIONAL_LABELS: Record<string, string> = {
  mistral: k("Mistral"),
  mistralTramontane: k("Mistral ou tramontane"),
  tramontane: k("Tramontane"),
  autan: k("Vent d'autan"),
};

const ASSESSMENTS: Record<string, string> = {
  "très favorable": k("très favorable"),
  favorable: k("favorable"),
  "plutôt favorable": k("plutôt favorable"),
  "plutôt défavorable": k("plutôt défavorable"),
  défavorable: k("défavorable"),
  "très défavorable": k("très défavorable"),
};

export function skyLabel(id: string): string {
  return t(SKY_LABELS[id] ?? id);
}

/** Vent d'une tuile ; contexte « vent » : « Faible » s'y dit autrement qu'ailleurs en anglais (une force de vent). */
export function windLabel(id: string, regional?: string | null): string {
  if (id === "regional" && regional && REGIONAL_LABELS[regional]) return t(REGIONAL_LABELS[regional]);
  return tc("vent", WIND_LABELS[id] ?? id);
}

/** Vent dans une phrase : « vent faible », « tempête », le nom du vent régional en minuscules (« mistral »). */
function windPhrase(id: string, regional?: string | null): string {
  if (id === "regional" && regional && REGIONAL_LABELS[regional]) return t(REGIONAL_LABELS[regional]).toLocaleLowerCase(locale());
  return t(WIND_PHRASES[id] ?? id);
}

export function assessmentLabel(value: string): string {
  return t(ASSESSMENTS[value] ?? value);
}

/** Libellé du pictogramme de l'aperçu : ciel du sélecteur ou clé propre au jeu saisonnier. */
export function pictogramLabel(key: string): string {
  return PICTOGRAM_LABELS[key] ? t(PICTOGRAM_LABELS[key]) : skyLabel(key);
}

export function climateLabel(zone: string): string {
  return CLIMATE_LABELS[zone] ? t(CLIMATE_LABELS[zone]) : zone;
}

/** Ton du badge d'appréciation (rang 0 très favorable à 5 très défavorable). */
export function assessmentTone(severity: number): "ok" | "neutral" | "warn" | "danger" {
  if (severity <= 1) return "ok";
  if (severity === 2) return "neutral";
  return severity >= 5 ? "danger" : "warn";
}

/** Nombre à la manière de la langue lue (« 1 000 », « 0,5 »). */
export function num(value: number): string {
  return value.toLocaleString(locale(), { maximumFractionDigits: 1 });
}

/** Fourchette « 15 à 30 » (une seule valeur si les bornes sont égales). */
export function span(lo: number, hi: number, unit: string): string {
  if (unit === "°C") return lo === hi ? t("{v} °C", { v: num(lo) }) : t("{lo} à {hi} °C", { lo: num(lo), hi: num(hi) });
  return lo === hi ? `${num(lo)} ${unit}` : t("{lo} à {hi} {unit}", { lo: num(lo), hi: num(hi), unit });
}

/** Visibilité de l'aperçu : fourchette, ou « plus de 15 km » quand le jeu ne donne qu'un seuil bas. */
export function visibilityText(range: [number, number]): string {
  return range[0] === range[1] ? t("plus de {v} km", { v: num(range[0]) }) : span(range[0], range[1], "km");
}

/** Grandeurs de la météo appliquée, rédigées de même dans l'aperçu du réglage et dans la rubrique Météo de l'analyse
 *  du terrain : plafond (« Ciel dégagé » sans plafond), vent (« 10 à 25 km/h »), température (« 2 à 8 °C »). */
export function ceilingText(ft: number | null): string {
  return ft === null ? t("Ciel dégagé") : `${num(ft)} ft`;
}

export function windText(range: [number, number]): string {
  return span(range[0], range[1], "km/h");
}

export function temperatureText(range: [number, number]): string {
  return span(range[0], range[1], "°C");
}

// --- États et identifiant ---------------------------------------------------------------------------------------

type WeatherParams = Pick<ExerciseParameters, "weather_scenario" | "weather_custom"> & { level?: ExerciseParameters["level"] };

/** Trois états de la météo enregistrée : saisonnière (« auto », vide, ou valeur que le serveur ne lit pas : clé
 *  « custom:… » écrite telle quelle), choisie (« custom » et son choix), scénario du catalogue importé (tout autre
 *  identifiant, ciel compris : il n'est pas un choix composé, ses textes sont ceux du catalogue). */
export function weatherState(params: Pick<ExerciseParameters, "weather_scenario" | "weather_custom">): "seasonal" | "custom" | "imported" {
  const id = params.weather_scenario;
  if (!id || id === SEASONAL || id.startsWith(`${CUSTOM}:`)) return "seasonal";
  if (id === CUSTOM) return params.weather_custom ? "custom" : "seasonal";
  return "imported";
}

/** Choix « Choisie » des paramètres, null pour la météo saisonnière ou un scénario du catalogue. */
export function customOf(params: Pick<ExerciseParameters, "weather_scenario" | "weather_custom">): WeatherCustom | null {
  return weatherState(params) === "custom" ? params.weather_custom! : null;
}

/** Identifiant de scénario lu par le serveur (`environment.weather_key`) : « auto » en CENTAC (la météo y est celle de
 *  la saison, retouchée dans l'ordre de la rotation) et pour ce que le serveur lit comme la saisonnière. */
export function weatherKey(params: WeatherParams | null | undefined): string | null {
  if (!params) return null;
  if (params.level === "CENTAC") return SEASONAL;
  const state = weatherState(params);
  if (state === "seasonal") return params.weather_scenario ? SEASONAL : null;
  if (state === "imported") return params.weather_scenario;
  const c = params.weather_custom!;
  const lo = c.temp_min ?? "";
  const hi = c.temp_max ?? "";
  return `custom:${c.sky}:${c.wind ?? WIND_FROM_SKY}:${lo}:${hi}`;
}

/** Clé du cache des options d'une carte : terrain (état et climat de la zone, relus au « Terrain prêt » et après un
 *  changement de climat), date, fuseau, météo envoyée, et dossier affiché (sa création date un exercice sans date :
 *  deux dossiers sans date sur la même carte n'ont pas le même aperçu ; null pour un brouillon à créer). */
export function weatherOptionsKey(
  mapSlug: string | null,
  zone: { status: string; climate: string } | null | undefined,
  startDtg: string | null,
  timeZone: string | null,
  key: string | null,
  exerciseId: string | null = shownDossierId(),
) {
  return ["weather-options", mapSlug, zone?.status ?? null, zone?.climate ?? null, startDtg, timeZone, key, exerciseId] as const;
}

/** Clé du cache des options d'un lieu nommé (nouveau terrain pas encore placé, `POST /places/weather`) : mêmes
 *  rangs que `weatherOptionsKey`, « place » à la place de la carte, puis les coordonnées. */
export function placeWeatherKey(
  place: Pick<NamedPlace, "lon" | "lat">,
  startDtg: string | null,
  timeZone: string | null,
  key: string | null,
  exerciseId: string | null = shownDossierId(),
) {
  return ["weather-options", "place", place.lon, place.lat, startDtg, timeZone, key, exerciseId] as const;
}

/** Lieu d'un verdict, comparé d'un verdict à l'autre (`WeatherContext.map`) : la carte, ou « place:lon,lat » pour un
 *  lieu nommé. */
export function placeContext(place: Pick<NamedPlace, "lon" | "lat">): string {
  return `place:${place.lon},${place.lat}`;
}

/** Vent d'un panache NRBC à poser sur la carte `mapSlug` : celui de la météo ENREGISTRÉE du dossier affiché, lu dans
 *  sa seule entrée d'options Météo (jamais un choix essayé puis abandonné dans le réglage, ni la météo d'un autre
 *  dossier de la même carte) ; à défaut, et en CENTAC (aucun réglage), celui de l'iPad (dérive vers l'est,
 *  10 nœuds), à régler dans la fiche. `zone` : terrain de la carte (état et climat de la clé du cache). */
export function plumeWind(
  queryClient: QueryClient,
  mapSlug: string | null,
  exercise: { id: string; map_slug: string | null; parameters: ExerciseParameters } | null | undefined,
  zone: { status: string; climate: string } | null | undefined,
): { bearing: number; knots: number } {
  const fallback = { bearing: 90, knots: 10 };
  const p = exercise?.parameters;
  if (!mapSlug || !exercise || !p || exercise.map_slug !== mapSlug || p.level === "CENTAC") return fallback;
  const key = weatherOptionsKey(mapSlug, zone, p.start_dtg, p.time_zone, weatherKey(p), exercise.id);
  return queryClient.getQueryData<WeatherOptions>(key)?.plume ?? fallback;
}

// --- Températures -----------------------------------------------------------------------------------------------

/** Motif de refus d'une température choisie (mêmes règles que le serveur), null si elle est admise. Un champ vide
 *  (NaN) est un champ absent, jamais 0 °C. */
export function temperatureProblem(sky: string, lo: number, hi: number, bounds: [number, number]): string | null {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return t("Indiquez la minimale et la maximale.");
  if (lo > hi) return t("La minimale doit être au plus égale à la maximale.");
  if (lo < bounds[0] || hi > bounds[1]) {
    return t("Température peu plausible ici : de {lo} à {hi} °C pour ce climat et cette saison.", { lo: bounds[0], hi: bounds[1] });
  }
  if (hi - lo > 25) return t("Écart trop grand : 25 °C au plus entre minimale et maximale.");
  if (FROZEN.has(sky) && lo > 1) return t("Neige, verglas ou givre : minimale au plus de 1 °C.");
  if (LIQUID.has(sky) && hi < 1) return t("Pluie ou orage : maximale d'au moins 1 °C.");
  return null;
}

/** Saisie d'un champ de degrés : nombre, ou NaN pour un champ vide (jamais 0 °C). */
export function degrees(text: string): number {
  return text.trim() === "" ? Number.NaN : Number(text);
}

const FROZEN = new Set(["brouillard_givrant", "neige_legere", "neige_forte", "verglas", "blizzard"]);
const LIQUID = new Set(["crachin", "pluie", "pluie_forte", "orage", "cevenol", "mousson", "orage_crue"]);

/** Minimale et maximale proposées au passage à « Minimale et maximale » : celles de la saison pour ce ciel, ramenées
 *  dans les règles (plage plausible, minimale au plus de 1 °C sous la neige, maximale d'au moins 1 °C sous la pluie,
 *  25 °C d'écart au plus) ; null si aucune paire admise n'en découle (champs ouverts vides, rien d'écrit). */
export function admittedTemperature(sky: string, pair: [number, number] | null | undefined, bounds: [number, number]): [number, number] | null {
  if (!pair) return null;
  let lo = Math.max(Math.round(pair[0]), bounds[0]);
  let hi = Math.min(Math.round(pair[1]), bounds[1]);
  if (FROZEN.has(sky)) lo = Math.min(lo, 1);
  if (LIQUID.has(sky)) hi = Math.max(hi, 1);
  if (hi < lo) hi = lo;
  if (hi - lo > 25) lo = hi - 25;
  return temperatureProblem(sky, lo, hi, bounds) === null ? [lo, hi] : null;
}

// --- Conditions possibles ---------------------------------------------------------------------------------------

/** Vents proposés avec un ciel : ceux de la carte compatibles avec lui (`environment.wind_fits_sky`). */
export function windsFor(sky: string, options: Pick<WeatherOptions, "wind" | "wind_by_sky">): string[] {
  return options.wind_by_sky?.[sky] ?? options.wind;
}

/** Choix après un changement de ciel : le vent qui ne va pas avec lui repasse à « selon le ciel », la température
 *  qui ne va pas avec lui (neige au-dessus de 1 °C, pluie sous zéro) à celle de la saison. `adjusted` : ce qui a
 *  été repris, pour le dire. */
export function withSky(
  choice: WeatherCustom,
  sky: string,
  options: Pick<WeatherOptions, "wind" | "wind_by_sky" | "temp_bounds">,
): { next: WeatherCustom; adjusted: ("wind" | "temperature")[] } {
  const adjusted: ("wind" | "temperature")[] = [];
  let next: WeatherCustom = { ...choice, sky };
  if (!windsFor(sky, options).includes(next.wind ?? WIND_FROM_SKY)) {
    next = { ...next, wind: WIND_FROM_SKY };
    adjusted.push("wind");
  }
  if (next.temp_min !== null && next.temp_max !== null && temperatureProblem(sky, next.temp_min, next.temp_max, options.temp_bounds) !== null) {
    next = { ...next, temp_min: null, temp_max: null };
    adjusted.push("temperature");
  }
  return { next, adjusted };
}

/** Le choix reste-t-il possible pour la carte et le mois ? */
export function stillPossible(choice: WeatherCustom, options: WeatherOptions): boolean {
  if (!options.sky.includes(choice.sky) || !windsFor(choice.sky, options).includes(choice.wind ?? WIND_FROM_SKY)) return false;
  if (choice.temp_min === null || choice.temp_max === null) return true;
  return temperatureProblem(choice.sky, choice.temp_min, choice.temp_max, options.temp_bounds) === null;
}

/** Ciel de « Choisie » le plus proche de la météo de saison (signalé sur sa tuile, jamais écrit) : celui du
 *  pictogramme saisonnier s'il est proposé, clair par chaleur ou par froid, pluie pour des averses ou le dégel. */
export function seasonalSky(options: Pick<WeatherOptions, "sky" | "preview">): string | null {
  const pictogram = options.preview?.pictogram ?? "";
  const near: Record<string, string> = { chaleur: "clair", froid: "clair", vent: "clair", averses: "pluie", degel: "pluie" };
  const sky = options.sky.includes(pictogram) ? pictogram : near[pictogram];
  return sky && options.sky.includes(sky) ? sky : null;
}

// --- Libellés composés ------------------------------------------------------------------------------------------

/** Choix en une ligne : « Brouillard, vent faible, 2 à 8 °C » (vent du ciel et température de saison omis). */
export function choiceLabel(choice: WeatherCustom, regional?: string | null): string {
  const parts = [skyLabel(choice.sky)];
  if (choice.wind && choice.wind !== WIND_FROM_SKY) parts.push(windPhrase(choice.wind, regional));
  if (choice.temp_min !== null && choice.temp_max !== null) parts.push(span(choice.temp_min, choice.temp_max, "°C"));
  return parts.join(", ");
}

/** Choix d'un identifiant « custom:ciel:vent:min:max » (`environment.custom_key`), null sinon. */
export function parseCustomKey(key: string | null | undefined): WeatherCustom | null {
  const parts = key?.split(":");
  if (!parts || parts.length !== 5 || parts[0] !== CUSTOM || !parts[1]) return null;
  const temp = (value: string) => (value === "" ? null : Number(value));
  const lo = temp(parts[3]!);
  const hi = temp(parts[4]!);
  if ((lo !== null && !Number.isFinite(lo)) || (hi !== null && !Number.isFinite(hi))) return null;
  return { sky: parts[1], wind: parts[2] || WIND_FROM_SKY, temp_min: lo, temp_max: hi };
}

/** Libellé de la météo APPLIQUÉE (`weather_settle.applied_key` d'une réponse du serveur), dans la langue de
 *  l'interface : un choix composé se nomme comme les tuiles, un scénario du catalogue par le libellé du serveur
 *  (`serverLabel`, vent régional déjà nommé d'après la carte) ; null pour la météo saisonnière (« auto »), que rien
 *  ne nomme à part. */
export function appliedWeatherLabel(key: string | null | undefined, serverLabel?: string | null, regional?: string | null): string | null {
  if (!key || key === SEASONAL) return null;
  const choice = parseCustomKey(key);
  if (choice) return choiceLabel(choice, regional);
  return serverLabel && serverLabel !== SEASONAL ? serverLabel : null;
}

/** Pictogramme d'une météo appliquée connue par son identifiant (`environment.pictogram`) : le ciel d'un choix ou
 *  d'un scénario de ciel, vent, chaleur ou froid pour les scénarios du catalogue sans ciel ; null pour la
 *  saisonnière, dont le pictogramme vient du serveur (jeu de la saison). */
export function pictogramOfKey(key: string | null | undefined): string | null {
  if (!key || key === SEASONAL) return null;
  const choice = parseCustomKey(key);
  if (choice) return choice.sky;
  if (key in SKY_LABELS) return key;
  if (key.startsWith("vent") || key === "tempete") return "vent";
  if (key === "canicule" || key === "chaleur_extreme") return "chaleur";
  if (key === "froid_sec" || key === "grand_froid" || key === "froid_extreme") return "froid";
  return null;
}

/** Libellé d'un scénario du catalogue envoyé (dossier importé), dans la langue de l'interface. */
export function scenarioLabel(options: Pick<WeatherOptions, "scenario">): string | null {
  const sc = options.scenario;
  if (!sc) return null;
  return lang() === "en" ? sc.label_en : sc.label_fr;
}

// --- Écart entre le choix enregistré et la météo appliquée -------------------------------------------------------

/** Ce que les ordres font du choix envoyé, d'après le verdict du serveur : rien à dire (null), choix impossible ici
 *  (météo de saison appliquée), ou vent et température repris de la saison (`reverted`). */
export type WeatherGap = { kind: "impossible" } | { kind: "reverted"; parts: ("wind" | "temperature")[]; settled: WeatherCustom | null };

export function weatherGap(options: Pick<WeatherOptions, "valid" | "reverted" | "settled">, sent: string | null): WeatherGap | null {
  if (!sent || sent === SEASONAL) return null;
  if (!options.valid) return { kind: "impossible" };
  if (options.reverted.length) return { kind: "reverted", parts: options.reverted, settled: options.settled };
  return null;
}

/** Texte de l'écart, au présent (réglage ouvert sur un choix devenu inapplicable, avis « Terrain prêt »). */
export function weatherGapText(gap: WeatherGap): string {
  if (gap.kind === "impossible") return t("Cette météo n'est pas possible sur ce terrain à cette date : les ordres appliquent la météo de saison.");
  if (gap.parts.length === 2) return t("Les ordres appliquent le vent du ciel et la température de saison : ceux choisis ne conviennent pas ici.");
  return gap.parts[0] === "wind"
    ? t("Les ordres appliquent le vent du ciel : le vent choisi n'est pas possible ici.")
    : t("Les ordres appliquent la température de saison : celle choisie n'est pas plausible ici.");
}

/** Cause d'un réajustement du brouillon : terrain changé, lieu nommé du nouveau terrain choisi ou changé, ou date
 *  (mois de l'exercice) changée dans ce brouillon. */
export type ReadjustCause = "terrain" | "place" | "date";

/** Réajustement d'un choix devenu inapplicable après un changement de lieu, de date ou de fuseau fait dans le
 *  brouillon (jamais à la simple ouverture) : la météo de saison si le choix n'est plus possible, sinon le choix tel
 *  que les ordres l'appliquent (vent ou température repris de la saison). null : rien à réajuster. */
export function readjusted(
  options: Pick<WeatherOptions, "valid" | "reverted" | "settled">,
  sent: string | null,
): { scenario: string; custom: WeatherCustom | null; adjusted: ("wind" | "temperature")[] } | null {
  const gap = weatherGap(options, sent);
  if (!gap) return null;
  if (gap.kind === "impossible" || !gap.settled) return { scenario: SEASONAL, custom: null, adjusted: [] };
  return { scenario: CUSTOM, custom: gap.settled, adjusted: gap.parts };
}

/** Contexte d'un verdict du serveur : carte (ou lieu nommé, `placeContext`) et mois de l'exercice (ceux de l'OPO). */
export interface WeatherContext {
  map: string;
  month: number;
}

/** Réajustement à faire quand un verdict sûr arrive pour un brouillon : rien au premier contexte vu (ouverture des
 *  Données initiales, ou montage du réglage), rien si ni la carte ni le mois n'ont changé depuis le verdict d'avant
 *  (climat changé, fin de préparation : l'écart est seulement signalé) ; sinon le réajustement d'un choix devenu
 *  inapplicable et sa cause (terrain si la carte a changé, sinon date). */
export function readjustOnContext(
  before: WeatherContext | null,
  context: WeatherContext,
  options: Pick<WeatherOptions, "valid" | "reverted" | "settled">,
  sent: string | null,
): (NonNullable<ReturnType<typeof readjusted>> & { cause: ReadjustCause }) | null {
  if (!before || (before.map === context.map && before.month === context.month)) return null;
  const next = readjusted(options, sent);
  if (!next) return null;
  const cause: ReadjustCause = before.map === context.map ? "date" : context.map.startsWith("place:") ? "place" : "terrain";
  return { ...next, cause };
}

/** Message d'un réajustement, qui dit sa vraie cause (le lieu ou la date, jamais « ce ciel »). */
export function readjustText(adjusted: ("wind" | "temperature")[], cause: ReadjustCause): string {
  if (cause === "place") {
    if (adjusted.length === 0) return t("Météo remise sur « Saisonnière » : celle choisie n'est pas possible à cet endroit.");
    if (adjusted.length === 2) return t("Vent et température remis sur la saison : ceux choisis ne conviennent pas à cet endroit.");
    return adjusted[0] === "wind"
      ? t("Vent remis sur « Selon le ciel » : celui choisi n'existe pas à cet endroit.")
      : t("Température remise sur « Saisonnière » : celle choisie n'est pas plausible à cet endroit.");
  }
  const terrain = cause === "terrain";
  if (adjusted.length === 0)
    return terrain
      ? t("Météo remise sur « Saisonnière » : celle choisie n'est pas possible sur ce terrain.")
      : t("Météo remise sur « Saisonnière » : celle choisie n'est pas possible sur ce terrain à cette date.");
  if (adjusted.length === 2)
    return terrain
      ? t("Vent et température remis sur la saison : ceux choisis ne conviennent pas à ce terrain.")
      : t("Vent et température remis sur la saison : ceux choisis ne conviennent pas à ce terrain à cette date.");
  if (adjusted[0] === "wind")
    return terrain
      ? t("Vent remis sur « Selon le ciel » : celui choisi n'existe pas sur ce terrain.")
      : t("Vent remis sur « Selon le ciel » : celui choisi n'est pas possible sur ce terrain à cette date.");
  return terrain
    ? t("Température remise sur « Saisonnière » : celle choisie n'est pas plausible sur ce terrain.")
    : t("Température remise sur « Saisonnière » : celle choisie n'est pas plausible sur ce terrain à cette date.");
}

// --- Aperçu -----------------------------------------------------------------------------------------------------

/** Seuils de l'état du vol, ceux du serveur (`environment.flight_status`) : seulement pour en dire les causes. */
const LOW_CEILING_FT = 1000;
const LOW_VISIBILITY_KM = 3;
const STRONG_WIND_KMH = 45;

/** Effet de la météo sur les moyens aériens (`preview.flight`), avec ses vraies causes : plafond, visibilité, vent,
 *  givrage, orages ; null par temps qui ne gêne pas le vol. Hélicoptères, drones tactiques et appui aérien seulement
 *  (chantier « Drones ») : les petits drones ont leur propre ligne (`smallDronesEffect`), qu'elle ne contredit
 *  jamais. */
export function flightEffect(p: NonNullable<WeatherOptions["preview"]>): string | null {
  if (p.flight === "ok") return null;
  const causes: string[] = [];
  if (p.ceiling_ft !== null && p.ceiling_ft < LOW_CEILING_FT) causes.push(t("plafond {ft} ft", { ft: num(p.ceiling_ft) }));
  if (p.visibility_km[0] < LOW_VISIBILITY_KM) causes.push(t("visibilité {km} km", { km: num(p.visibility_km[0]) }));
  if (p.wind_kmh[1] >= STRONG_WIND_KMH) causes.push(t("vent {kmh} km/h", { kmh: num(p.wind_kmh[1]) }));
  if (p.flags.includes("icing")) causes.push(t("givrage"));
  if (p.flags.includes("storm")) causes.push(t("orages"));
  const why = causes.join(", ");
  if (p.flight === "suspended")
    return why
      ? t("Hélicoptères, drones tactiques et appui aérien suspendus : {causes}.", { causes: why })
      : t("Hélicoptères, drones tactiques et appui aérien suspendus.");
  return why
    ? t("Hélicoptères, drones tactiques et appui aérien contraints : {causes}.", { causes: why })
    : t("Hélicoptères, drones tactiques et appui aérien contraints.");
}

/** Ligne d'aperçu : « Brouillard, 2 à 8 °C, visibilité 0,5 à 2 km — défavorable », tirée des seules options reçues
 *  (jamais un nouveau ciel avec les chiffres de l'ancien, le temps d'une relecture). */
export function previewLine(options: WeatherOptions): string {
  const p = options.preview;
  if (!p) return "";
  const head = pictogramLabel(p.pictogram);
  const vis = p.visibility_km[0] === p.visibility_km[1]
    ? t("visibilité {v} km", { v: num(p.visibility_km[0]) })
    : t("visibilité {lo} à {hi} km", { lo: num(p.visibility_km[0]), hi: num(p.visibility_km[1]) });
  return t("{sky}, {lo} à {hi} °C, {visibility} — {assessment}", {
    sky: head,
    lo: p.temp[0],
    hi: p.temp[1],
    visibility: vis,
    assessment: assessmentLabel(p.assessment),
  });
}

/** Nom de la nuit sombre et de sa raison (`ephemeris.darkness`), le même dans l'aperçu Météo et les modes d'action :
 *  « Nuit sombre · Lune couchée », « Nuit sombre · ciel couvert », « Nuit sombre · Lune 12 % » ; raison inconnue :
 *  « Nuit sombre ». */
export function darkNightText(reason: string | null | undefined, moonPct: number | null | undefined): string {
  const why =
    reason === "moonless"
      ? t("Lune couchée")
      : reason === "overcast"
        ? t("ciel couvert")
        : reason === "moon" && moonPct != null
          ? t("Lune {pct} %", { pct: moonPct })
          : null;
  return why ? `${t("Nuit sombre")} · ${why}` : t("Nuit sombre");
}

/** Pastille de la nuit du jour de l'heure H, d'après la définition unique de la nuit sombre (`ephemeris.darkness`) ;
 *  à défaut (serveur qui ne la calcule pas), l'ancienne nuit longue et peu éclairée. */
export function darkNightLabel(e: NonNullable<WeatherOptions["ephemeris"]>): string | null {
  if (e.darkness) {
    if (!e.darkness.dark) return null;
    return darkNightText(e.darkness.reason, e.darkness.moon_pct ?? e.moon_pct);
  }
  return e.dark_night ? darkNightText("moon", e.moon_pct) : null;
}

/** Ligne d'éphéméride : « Soleil 0546B – 2157B, lune éclairée à 45 % ». */
export function ephemerisLine(options: WeatherOptions): string | null {
  const e = options.ephemeris;
  if (!e) return null;
  let sun: string;
  if (e.sun_state === "up") sun = t("soleil levé toute la journée");
  else if (e.sun_state === "down") sun = t("soleil couché toute la journée");
  else sun = t("soleil {rise} – {set}", { rise: e.sunrise ?? "—", set: e.sunset ?? "—" });
  const night = e.bmnt && e.eent ? t(", crépuscule nautique {bmnt} – {eent}", { bmnt: e.bmnt, eent: e.eent }) : "";
  return t("Éphéméride : {sun}{night}, lune éclairée à {moon} %", { sun, night, moon: e.moon_pct });
}
